import React, { useState, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-community";
import { Search } from "lucide-react";
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Alert,
  Badge,
  Space,
  Divider,
  Row,
  Col,
  Statistic,
  notification,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  FileTextOutlined,
  BellOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { Trash2 } from "lucide-react";
import type { UploadFile } from "antd/es/upload/interface";
import dayjs from "dayjs";
import images from "../../../assets/images";
import "../../../styles/ag-grid-custom.css";
import { getAllLawsRegulationsFolders, getUserDirectory } from "../../../service/api";
import { useLawsAndRegulationsTaskStore } from "../../../store/store";
import { LawsAndRegulationsTask as LawTask } from "../../../types/api";

const { Option } = Select;
const { TextArea } = Input;

interface Alert {
  type: string;
  severity: "High" | "Critical";
  message: string;
  taskId: string;
  daysUntilDue?: number;
  daysOverdue?: number;
}

const LawsAndRegulationsTask: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState<string>("");
  const [editingItem, setEditingItem] = useState<LawTask | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const gridRef = useRef<AgGridReact<LawTask>>(null);

  // Use the store
  const {
    tasks,
    loading,
    error,
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
    searchTasks,
    filterTasks,
    completeTask,
    setCurrentTask,
    clearTasks,
  } = useLawsAndRegulationsTaskStore();

  // Laws data from database
  const [laws, setLaws] = useState<{ id: string; name: string }[]>([]);

  // Users data for policy tasks
  const [availableUsers, setAvailableUsers] = useState<
    Array<{ id: string; name: string; email: string; employeeId: string }>
  >([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);

  // Agency data for audit tasks
  const [agencies, setAgencies] = useState<
    Array<{ name: string; email: string }>
  >([
    { name: "Fire Safety Authority", email: "fire.authority@example.com" },
    { name: "Building Control Board", email: "building.control@example.com" },
    { name: "Environmental Protection Agency", email: "epa@example.com" },
    { name: "Health and Safety Executive", email: "hse@example.com" },
    { name: "Local Council", email: "council@example.com" },
  ]);

  // Fetch laws from API
  const fetchLaws = async () => {
    try {
      const response = await getAllLawsRegulationsFolders();
      if (response.data.resCode === 200) {
        const lawsData =
          response.data.message?.results || response.data.message || [];
        // Filter to ensure only laws with policyType: "laws_and_regulations"
        const filteredLaws = lawsData.filter((law: any) => {
          return law.policyType === "laws_and_regulations";
        });
        const transformedLaws = filteredLaws.map((law: any) => ({
          id: law.id || law._id,
          name: law.folderName || "N/A",
        }));
        setLaws(transformedLaws);
      }
    } catch (error) {
      console.error("Error fetching laws:", error);
      message.error("Failed to fetch laws and regulations");
    }
  };

  // Fetch users for policy tasks
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await getUserDirectory();
      const users = response.data?.data || [];

      const transformedUsers = users.map((user: any) => ({
        id: user._id || user.id,
        name:
          `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
          user.name ||
          "Unknown",
        email: user.email || "",
        employeeId: user.employeeId || "",
      }));

      setAvailableUsers(transformedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error("Failed to fetch users");
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch tasks from store
  const fetchTasksData = async () => {
    try {
      await fetchTasks();
    } catch (error) {
      console.error("Error fetching tasks:", error);
      message.error("Failed to fetch tasks");
    }
  };

  // Search tasks
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      await fetchTasks();
      return;
    }

    try {
      await searchTasks(searchQuery);
    } catch (error) {
      console.error("Error searching tasks:", error);
      message.error("Failed to search tasks");
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchLaws();
    fetchUsers();
    fetchTasksData();
  }, []);

  // Handle form field changes for dynamic form behavior
  const [form] = Form.useForm();

  // Watch for task type changes to show/hide relevant fields
  const taskType = Form.useWatch("taskType", form);

  // Handle agency selection to auto-fill email
  const handleAgencyChange = (agencyName: string) => {
    const selectedAgency = agencies.find(
      (agency) => agency.name === agencyName,
    );
    if (selectedAgency) {
      form.setFieldsValue({ agencyEmail: selectedAgency.email });
    }
  };

  // Clear form fields when task type changes
  useEffect(() => {
    if (taskType === "Policy") {
      form.setFieldsValue({ agencyName: undefined, agencyEmail: undefined });
    } else {
      form.setFieldsValue({ assignedTo: undefined });
    }
  }, [taskType, form]);

  // Restore original data when search is cleared
  useEffect(() => {
    if (!searchQuery.trim()) {
      fetchTasksData();
    }
  }, [searchQuery]);

  // Calculate statistics
  const totalTasks = tasks.length;
  const pendingTasks = tasks.filter((task) => task.status === "Pending").length;
  const overdueTasks = tasks.filter((task) => task.status === "Overdue").length;
  const completedTasks = tasks.filter(
    (task) => task.status === "Completed",
  ).length;

  // Get alerts for due dates
  const getAlerts = (): Alert[] => {
    const alerts: Alert[] = [];

    tasks.forEach((task) => {
      const dueDate = dayjs(task.dueDate);
      const now = dayjs();
      const thirtyDaysFromNow = now.add(30, "day");

      if (dueDate.isBefore(now) && task.status !== "Completed") {
        alerts.push({
          type: "Task Overdue",
          severity: "Critical",
          message: `Task "${task.taskTitle}" is overdue`,
          taskId: task.id || task._id || "",
          daysOverdue: now.diff(dueDate, "day"),
        });
      } else if (dueDate.isBefore(thirtyDaysFromNow) && dueDate.isAfter(now)) {
        alerts.push({
          type: "Task Due Soon",
          severity: "High",
          message: `Task "${task.taskTitle}" is due soon`,
          taskId: task.id || task._id || "",
          daysUntilDue: dueDate.diff(now, "day"),
        });
      }
    });

    return alerts;
  };

  const alerts: Alert[] = getAlerts();

  const showModal = (type: string, item?: LawTask) => {
    setModalType(type);
    setEditingItem(item || null);
    setIsModalVisible(true);
    if (item) {
      form.setFieldsValue({
        ...item,
        dueDate: item.dueDate ? dayjs(item.dueDate) : null,
        reminderDate: item.reminderDate ? dayjs(item.reminderDate) : null,
        taskType: item.taskType || "Policy",
        assignedTo: item.assignedTo?.name || item.assignedTo || "",
        agencyName: item.agencyName || "",
        agencyEmail: item.agencyEmail || "",
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        taskType: "Policy",
        status: "Pending",
        priority: "Medium",
        frequency: "One Time",
        reminderSet: true,
      });
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    try {
      // Loading is handled by the store

      // Get the selected law name from the laws array
      const selectedLaw = laws.find((law) => law.id === values.lawId);

      const formData = {
        lawId: values.lawId,
        lawName: selectedLaw?.name || values.lawId || "N/A",
        taskType: values.taskType,
        taskTitle: values.taskTitle,
        description: values.description,
        frequency: values.frequency,
        dueDate: values.dueDate ? values.dueDate.format("YYYY-MM-DD") : "",
        reminderDate: values.reminderDate
          ? values.reminderDate.format("YYYY-MM-DD")
          : "",
        priority: values.priority,
        status: values.status,
        assignedTo:
          values.taskType === "Policy"
            ? {
              name: values.assignedTo || "N/A",
              email:
                availableUsers.find((u) => u.id === values.assignedTo)
                  ?.email || "user@example.com",
            }
            : {
              name: values.agencyName || "N/A",
              email: values.agencyEmail || "agency@example.com",
            },
        agencyName: values.taskType === "Audit" ? values.agencyName : undefined,
        agencyEmail:
          values.taskType === "Audit" ? values.agencyEmail : undefined,
        attachments: [],
        remarks: values.remarks || "",
        reminderSet: true,
        premisesId: values.premisesId || "default-premises-id", // This should come from context
      };

      if (editingItem && editingItem.id) {
        await updateTask(editingItem.id, { ...formData, id: editingItem.id });
        message.success("Task updated successfully");
      } else {
        await createTask(formData);
        message.success("Task added successfully");
      }

      await fetchTasks();
      handleCancel();
    } catch (error) {
      console.error("Error saving task:", error);
      message.error("Failed to save task");
    }
  };

  const deleteTaskItem = async (taskId: string) => {
    try {
      await deleteTask(taskId);
      message.success("Task deleted successfully");
      await fetchTasks();
    } catch (error) {
      console.error("Error deleting task:", error);
      message.error("Failed to delete task");
    }
  };

  const updateTaskStatus = async (
    taskId: string,
    newStatus: LawTask["status"],
  ) => {
    try {
      await updateTask(taskId, { id: taskId, status: newStatus });
      message.success("Task status updated successfully");
      await fetchTasks();
    } catch (error) {
      console.error("Error updating task status:", error);
      message.error("Failed to update task status");
    }
  };

  const completeTaskItem = async (taskId: string) => {
    try {
      await completeTask(taskId);
      message.success("Task completed successfully");
      await fetchTasks();
    } catch (error) {
      console.error("Error completing task:", error);
      message.error("Failed to complete task");
    }
  };

  const toggleReminder = async (taskId: string) => {
    try {
      const task = tasks.find((t) => t.id === taskId);
      if (task) {
        await updateTask(taskId, {
          id: taskId,
          reminderSet: !task.reminderSet,
        });
        message.success("Reminder setting updated");
        await fetchTasks();
      }
    } catch (error) {
      console.error("Error updating reminder:", error);
      message.error("Failed to update reminder setting");
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Critical":
        return "red";
      case "High":
        return "orange";
      case "Medium":
        return "blue";
      case "Low":
        return "green";
      default:
        return "default";
    }
  };

  const handleView = (task: LawTask) => {
    showModal("view", task);
  };

  const handleEdit = (task: LawTask) => {
    showModal("edit", task);
  };

  const handleDelete = (taskId: string) => {
    deleteTaskItem(taskId);
  };

  const taskColumns: ColDef<LawTask>[] = [
    {
      headerName: "Law/Regulation",
      field: "lawName",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Task Type",
      field: "taskType",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
      cellRenderer: (params: any) => {
        const taskType = params.value;
        const getTaskTypeColor = (type: string) => {
          switch (type) {
            case "Policy":
              return "bg-blue-100 text-blue-800";
            case "Audit":
              return "bg-green-100 text-green-800";
            default:
              return "bg-gray-100 text-gray-800";
          }
        };
        return (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTaskTypeColor(taskType)}`}
          >
            {taskType}
          </span>
        );
      },
    },

    {
      headerName: "Frequency",
      field: "frequency",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Due Date",
      field: "dueDate",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: (params: any) => {
        const dueDate = dayjs(params.value);
        const now = dayjs();
        const isOverdue = dueDate.isBefore(now);
        const isApproaching =
          dueDate.isBefore(now.add(30, "day")) && dueDate.isAfter(now);

        if (isOverdue) {
          return {
            color: "red",
            padding: "10px 10px 0 10px",
            fontWeight: "bold",
          };
        } else if (isApproaching) {
          return {
            color: "orange",
            padding: "10px 10px 0 10px",
            fontWeight: "bold",
          };
        }
        return {
          color: "black",
          padding: "10px 10px 0 10px",
          fontWeight: "normal",
        };
      },
      valueFormatter: (params) => {
        return dayjs(params.value).format("DD/MM/YYYY");
      },
      cellRenderer: (params: any) => {
        const dueDate = dayjs(params.value);
        const now = dayjs();
        const isOverdue = dueDate.isBefore(now);
        const isApproaching =
          dueDate.isBefore(now.add(30, "day")) && dueDate.isAfter(now);

        let tooltipText = `Due Date: ${dueDate.format("DD/MM/YYYY")}`;
        if (isOverdue) {
          tooltipText += `\nStatus: OVERDUE (${now.diff(dueDate, "day")} days overdue)`;
        } else if (isApproaching) {
          tooltipText += `\nStatus: APPROACHING (${dueDate.diff(now, "day")} days remaining)`;
        } else {
          tooltipText += `\nStatus: ON TIME`;
        }

        return (
          <Tooltip
            title={tooltipText}
            overlayStyle={{ backgroundColor: "white", color: "red" }}
            overlayInnerStyle={{ backgroundColor: "white", color: "red" }}
          >
            <span className="cursor-help">
              {dayjs(params.value).format("DD/MM/YYYY")}
            </span>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Priority",
      field: "priority",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
      cellRenderer: (params: any) => {
        const priority = params.value;
        const getPriorityColor = (priority: string) => {
          switch (priority) {
            case "Critical":
              return "bg-red-100 text-red-800";
            case "High":
              return "bg-orange-100 text-orange-800";
            case "Medium":
              return "bg-blue-100 text-blue-800";
            case "Low":
              return "bg-green-100 text-green-800";
            default:
              return "bg-gray-100 text-gray-800";
          }
        };
        return (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(priority)}`}
          >
            {priority}
          </span>
        );
      },
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: (params) => {
        const status = params.value;
        if (status === "Completed")
          return {
            color: "green",
            padding: "10px 10px 0 10px",
            fontWeight: "bold",
          };
        if (status === "Overdue")
          return {
            color: "red",
            padding: "10px 10px 0 10px",
            fontWeight: "bold",
          };
        return {
          color: "#f59e0b",
          padding: "10px 10px 0 10px",
          fontWeight: "bold",
        };
      },
    },
    {
      headerName: "Assigned To/Agency",
      field: "assignedTo",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
      cellRenderer: (params: any) => {
        const task = params.data;
        if (task.taskType === "Policy") {
          return typeof task.assignedTo === "object"
            ? task.assignedTo.name
            : task.assignedTo;
        } else {
          return task.agencyName || "N/A";
        }
      },
    },
    {
      headerName: "Actions",
      flex: 1,
      headerClass: "bg-[#e9f5ff] text-center",
      cellStyle: { padding: "10px 10px 0 10px" },
      cellRenderer: (params: { data: LawTask }) => (
        <div className="flex items-center gap-2">
          <button onClick={() => handleView(params.data)}>
            <img className="iconTd" src={images.viewIcon} alt="View" />
          </button>
          <button onClick={() => handleEdit(params.data)}>
            <img className="iconTd" src={images.editIcon} alt="Edit" />
          </button>
          <button
            onClick={() =>
              handleDelete(params.data.id || params.data._id || "")
            }
            aria-label="Delete task"
          >
            <Trash2 className="text-red-500" size={20} />
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (tasks.length > 0) {
      setTimeout(() => {
        gridRef.current?.api.sizeColumnsToFit();
      }, 0);
    }
  }, [tasks]);

  return (
    <div className="px-2 py-4 max-w-8xl mx-auto mt-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-1">Tasks</h2>
          <div className="text-gray-500 text-base">
            Manage tasks related to laws, regulations, and compliance
            requirements
          </div>
        </div>
        <button
          className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
          onClick={() => showModal("add")}
        >
          <img src={images.addPremiessIcon} width={25} alt="" />
          ADD TASK
        </button>
      </div>

      {/* Statistics Dashboard */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Tasks"
              value={totalTasks}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Tasks"
              value={pendingTasks}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Overdue Tasks"
              value={overdueTasks}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Completed Tasks"
              value={completedTasks}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <div className="bg-[#ffffff] rounded-md shadow-md">
        <div className="p-4 flex justify-between items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search"
              className="block w-full pl-10 pr-3 py-2 border-2 border-[#a5b7cf] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
        </div>

        <div className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            rowData={tasks}
            columnDefs={taskColumns}
            rowHeight={50}
            headerHeight={65}
            domLayout="autoHeight"
            pagination={true}
            paginationPageSize={5}
            paginationPageSizeSelector={[5, 10, 20]}
            paginationAutoPageSize={false}
            defaultColDef={{
              sortable: true,
              resizable: true,
            }}
            loadingOverlayComponent={() => <div>Loading...</div>}
            loadingOverlayComponentParams={{}}
            overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Loading...</span>"
            overlayNoRowsTemplate="<span class='ag-overlay-no-rows-center'>No tasks found</span>"
          />
        </div>
      </div>

      {/* Task Modal */}
      <Modal
        title={
          modalType === "add"
            ? "Add Task"
            : modalType === "edit"
              ? "Edit Task"
              : "View Task Details"
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        className="rounded-lg overflow-hidden"
        maskClosable={false}
        closable={false}
        zIndex={1000001}
        styles={{
          header: {
            borderBottom: "2px solid #e5e7eb",
            borderRadius: "8px 8px 0 0",
          },
          body: {
            borderRadius: "0",
            padding: "0",
          },
        }}
      >
        {modalType === "view" && editingItem ? (
          <div className="p-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-lg text-gray-800 mb-4">
                Task Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Law/Regulation
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.lawName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Task Type
                    </label>
                    <div className="bg-gray-50 p-2 rounded border">
                      <Tag
                        color={
                          editingItem.taskType === "Policy" ? "blue" : "green"
                        }
                        style={{ marginLeft: 8 }}
                      >
                        {editingItem.taskType}
                      </Tag>
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Description
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.description}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Frequency
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.frequency}
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Due Date
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {dayjs(editingItem.dueDate).format("DD/MM/YYYY")}
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Priority
                    </label>
                    <div className="bg-gray-50 p-2 rounded border">
                      <Tag
                        color={getPriorityColor(editingItem.priority)}
                        style={{ marginLeft: 8 }}
                      >
                        {editingItem.priority}
                      </Tag>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-gray-600 font-semibold text-sm">
                  Status
                </label>
                <div className="bg-gray-50 p-2 rounded border">
                  <Badge
                    status={
                      editingItem.status === "Completed"
                        ? "success"
                        : editingItem.status === "Pending"
                          ? "processing"
                          : "error"
                    }
                    text={editingItem.status}
                  />
                </div>
              </div>
              {editingItem.taskType === "Policy" ? (
                <div className="mt-4">
                  <label className="block text-gray-600 font-semibold text-sm">
                    Assigned To
                  </label>
                  <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                    {typeof editingItem.assignedTo === "object"
                      ? editingItem.assignedTo.name
                      : editingItem.assignedTo}
                  </p>
                </div>
              ) : (
                <div className="mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-600 font-semibold text-sm">
                        Agency Name
                      </label>
                      <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                        {editingItem.agencyName || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-gray-600 font-semibold text-sm">
                        Agency Email
                      </label>
                      <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                        {editingItem.agencyEmail || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-4">
                <label className="block text-gray-600 font-semibold text-sm">
                  Remarks
                </label>
                <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                  {editingItem.remarks}
                </p>
              </div>
              <div className="mt-4">
                <label className="block text-gray-600 font-semibold text-sm">
                  Reminder Set
                </label>
                <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                  {editingItem.reminderSet ? "Yes" : "No"}
                </p>
              </div>
              {editingItem.daysRemaining && (
                <div className="mt-4">
                  <label className="block text-gray-600 font-semibold text-sm">
                    Days Remaining
                  </label>
                  <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                    {editingItem.daysRemaining}
                  </p>
                </div>
              )}
              {editingItem.attachments &&
                editingItem.attachments.length > 0 && (
                  <div className="mt-4">
                    <label className="block text-gray-600 font-semibold text-sm">
                      Attachments
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      Files uploaded: {editingItem.attachments.length} file(s)
                    </p>
                  </div>
                )}
            </div>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="lawId"
              label="Law/Regulation"
              rules={[
                { required: true, message: "Please select law/regulation" },
              ]}
            >
              <Select placeholder="Select law/regulation">
                {laws.map((law) => (
                  <Option key={law.id} value={law.id}>
                    {law.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="taskType"
              label="Task Type"
              rules={[{ required: true, message: "Please select task type" }]}
            >
              <Select placeholder="Select task type">
                <Option value="Policy">Policy</Option>
                <Option value="Audit">Audit</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[
                { required: true, message: "Please enter task description" },
              ]}
            >
              <Input.TextArea rows={3} placeholder="Enter task description" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="frequency"
                  label="Frequency"
                  rules={[
                    { required: true, message: "Please select frequency" },
                  ]}
                >
                  <Select placeholder="Select frequency">
                    <Option value="One Time">One Time</Option>
                    <Option value="Daily">Daily</Option>
                    <Option value="Weekly">Weekly</Option>
                    <Option value="Monthly">Monthly</Option>
                    <Option value="Quarterly">Quarterly</Option>
                    <Option value="Annually">Annually</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="priority"
                  label="Priority"
                  rules={[
                    { required: true, message: "Please select priority" },
                  ]}
                >
                  <Select placeholder="Select priority">
                    <Option value="Low">Low</Option>
                    <Option value="Medium">Medium</Option>
                    <Option value="High">High</Option>
                    <Option value="Critical">Critical</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="dueDate"
                  label="Due Date"
                  rules={[
                    { required: true, message: "Please select due date" },
                  ]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="reminderDate"
                  label="Reminder Date"
                  rules={[
                    { required: true, message: "Please select reminder date" },
                  ]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            {/* Conditional fields based on task type */}
            {taskType === "Policy" ? (
              <Form.Item
                name="assignedTo"
                label="Assigned To"
                rules={[
                  { required: true, message: "Please select assigned person" },
                ]}
              >
                <Select
                  placeholder="Select assigned person"
                  loading={loadingUsers}
                >
                  {availableUsers.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.name} ({user.email}) - {user.employeeId}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="agencyName"
                    label="Agency Name"
                    rules={[
                      { required: true, message: "Please enter agency name" },
                    ]}
                  >
                    <Select
                      placeholder="Select agency"
                      onChange={handleAgencyChange}
                    >
                      {agencies.map((agency) => (
                        <Option key={agency.name} value={agency.name}>
                          {agency.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="agencyEmail"
                    label="Agency Email"
                    rules={[
                      { required: true, message: "Please enter agency email" },
                    ]}
                  >
                    <Input placeholder="Enter agency email" />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Form.Item name="attachments" label="Upload Attachments">
              <Upload>
                <Button icon={<UploadOutlined />}>Upload Files</Button>
              </Upload>
            </Form.Item>

            <Form.Item name="remarks" label="Remarks">
              <Input.TextArea
                rows={3}
                placeholder="Enter any additional remarks"
              />
            </Form.Item>
          </Form>
        )}

        {/* Custom Footer with Styled Buttons */}
        <div className="flex bg-[#61748E] px-3 py-2 justify-end gap-4 -mx-6 -mb-6">
          {modalType === "view" ? (
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-400 text-white rounded-full hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => form.submit()}
                disabled={loading}
                className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors disabled:opacity-50"
              >
                {loading
                  ? editingItem
                    ? "Updating..."
                    : "Adding..."
                  : editingItem
                    ? "Update"
                    : "Add"}
              </button>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default LawsAndRegulationsTask;
